/**
 * Heartbeat monitor for BrowserWebSocketClientAdapter.
 *
 * The browser WebSocket API doesn't expose ping/pong frames, so we can't
 * directly detect server-sent pings. Instead, this monitor tracks message
 * activity on the adapter: any Automerge sync message counts as proof of
 * liveness. If no messages arrive within `timeout` ms, the adapter's
 * underlying socket is force-closed, triggering the built-in reconnect logic.
 *
 * This also hooks `visibilitychange` to immediately check liveness when the
 * tab returns to the foreground (laptops waking from sleep, tab switching).
 */
import type { BrowserWebSocketClientAdapter } from "@automerge/automerge-repo-network-websocket";

export type HeartbeatMonitorOptions = {
  /** Max ms of silence before the connection is considered dead. Default: 30000 */
  timeout?: number;
  /** Interval between liveness checks. Default: 10000 */
  checkInterval?: number;
};

export class WebSocketHeartbeatMonitor {
  #adapter: BrowserWebSocketClientAdapter;
  #timeout: number;
  #checkInterval: number;
  #lastActivity = Date.now();
  #timer?: ReturnType<typeof setInterval>;
  #onMessage = () => {
    this.#lastActivity = Date.now();
  };
  #onVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      this.#check();
    }
  };

  constructor(
    adapter: BrowserWebSocketClientAdapter,
    opts?: HeartbeatMonitorOptions,
  ) {
    this.#adapter = adapter;
    this.#timeout = opts?.timeout ?? 30_000;
    this.#checkInterval = opts?.checkInterval ?? 10_000;
  }

  start() {
    this.#lastActivity = Date.now();

    // Every incoming message is proof of liveness
    this.#adapter.on("message", this.#onMessage);

    // Peer connection also counts as activity
    this.#adapter.on("peer-candidate", this.#onMessage);

    // Periodic liveness check
    this.#timer = setInterval(() => this.#check(), this.#checkInterval);

    // Check immediately when tab becomes visible again
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", this.#onVisibilityChange);
    }
  }

  stop() {
    this.#adapter.off("message", this.#onMessage);
    this.#adapter.off("peer-candidate", this.#onMessage);
    if (this.#timer) {
      clearInterval(this.#timer);
      this.#timer = undefined;
    }
    if (typeof document !== "undefined") {
      document.removeEventListener(
        "visibilitychange",
        this.#onVisibilityChange,
      );
    }
  }

  #check() {
    const elapsed = Date.now() - this.#lastActivity;
    if (elapsed < this.#timeout) return;

    console.warn(
      `automerge-ws: no activity for ${Math.round(elapsed / 1000)}s, forcing reconnect`,
    );

    // Force-close the underlying socket. The adapter's onClose handler
    // will emit peer-disconnected and schedule a reconnect automatically.
    const socket = (this.#adapter as unknown as { socket?: WebSocket }).socket;
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.close();
    }

    // Reset so we don't spam close attempts
    this.#lastActivity = Date.now();
  }
}
